import { useRef, forwardRef, useEffect, useState } from 'react';
import type { FunctionComponent, Ref, PropsWithoutRef } from 'react';
import styled from 'styled-components';

import type { ForwardProps } from '../../../types';
import FormField from '../../FormField';
import Flex from '../../Flex';
import Select, { Option } from '../../Select';
import Icon, { registerIcon } from '../../Icon';
import * as clockIcon from '../../Icon/icons/clock.icon';
import Popover from '../../Popover';
import TimePicker from '../Picker/TimePicker';
import {
  useConfiguration,
  useConsolidatedRef,
  useFocusWithin,
  useI18n,
  useTestIds
} from '../../../hooks';
import { StyledFormControl } from '../../FormControl';
import type { FormControlProps } from '../../FormControl';
import type DateTimeProps from '../DateTime.types';
import type { DateTimeCallbackParameter } from '../DateTime.types';
import { sameWidth } from '../../Popover/modifiers';
import { createClassName, hasProp, withTestIds } from '../../../utils';
import type { MinuteInterval } from '../Picker/DatePicker.types';
import { getTimeInputTestIds } from '../DateTime.test-ids';
import { getAdjustedUTCDateTime } from '../utils';

import {
  getDateFormat,
  getDateTimeParts,
  parseToDate,
  is12HClockFormat,
  validateTime,
  getTimeOptions
} from './utils';
import type { ClockFormat } from './utils';
import StyledDateTime, { StyledInputContainer, StyledInputButton } from './DateTime.styles';
import PartInput from './PartInput';
import usePickerButton from './usePickerButton';
import useAutoFocusNextInput from './useAutoFocusNextInput';
import type { TimeParts } from './DateTimeInput.types';

registerIcon(clockIcon);

export interface TimeInputProps extends DateTimeProps {
  /**
   * Display the time with seconds.
   * @default false
   */
  withSeconds?: boolean;
  /**
   * Defines interval for time picker options (in minutes).
   * @default 30
   */
  pickerInterval?: MinuteInterval;
  /** Defines clock format that overrides locale based format */
  clockFormat?: ClockFormat;
  /** Pass a heading and content to show additional information on the field. */
  additionalInfo?: FormControlProps['additionalInfo'];
}

export const StyledTimeInput = styled(StyledDateTime)<TimeInputProps>`
  padding-inline-end: 0;
`;

function getMeridiem(hour: number | undefined) {
  return hour && hour >= 12 ? 'PM' : 'AM';
}

const convertTo12h = (hour: number) => {
  let converted: number = hour;
  if (hour > 12) converted = hour - 12;
  else if (hour === 0) converted = 12;
  return converted;
};

const convertTo24h = (hour: number, meridiem: string) => {
  let converted: number = hour;
  if (meridiem === 'PM' && hour < 12) converted = hour + 12;
  else if (meridiem === 'AM' && hour === 12) converted = 0;
  return converted;
};

const convertToCallbackParameter = (
  date: Date,
  parts: TimeParts<string | undefined>
): DateTimeCallbackParameter => {
  const resultDate = Number.isNaN(date.getTime())
    ? new Date(new Date().setUTCHours(0, 0, 0, 0))
    : new Date(date);
  const withSeconds = hasProp(parts, 'second');
  const {
    hour,
    minute,
    second = resultDate.getUTCSeconds().toString(),
    dayPeriod: meridiem
  } = parts;
  const toValidate = withSeconds ? [hour, minute, second] : [hour, minute];

  if (toValidate.every(Boolean)) {
    const is12h = meridiem !== undefined;

    if (!validateTime(Number(hour), Number(minute), Number(second), 0, is12h)) {
      return {
        valueAsISOString: '',
        valueAsTimestamp: NaN,
        state: 'invalid'
      };
    }

    resultDate.setUTCHours(
      is12h ? convertTo24h(Number(hour), meridiem) : Number(hour),
      Number(minute),
      Number(second),
      date.getUTCMilliseconds() || 0
    );
    return {
      valueAsISOString: resultDate.toISOString(),
      valueAsTimestamp: resultDate.getTime()
    };
  }
  if ([hour, minute].every(p => !p)) {
    return {
      valueAsISOString: undefined,
      valueAsTimestamp: undefined
    };
  }
  return {
    valueAsISOString: '',
    valueAsTimestamp: NaN,
    state: 'incomplete'
  };
};

const TimeInput: FunctionComponent<TimeInputProps & ForwardProps> = forwardRef(function TimeInput(
  props: PropsWithoutRef<TimeInputProps>,
  ref: Ref<HTMLFieldSetElement>
) {
  const { locale } = useConfiguration();
  const t = useI18n();
  const {
    testId,
    value,
    id,
    label,
    labelHidden,
    status,
    withSeconds = false,
    pickerInterval = 30,
    clockFormat,
    info = getDateFormat(t, locale, withSeconds ? 'time+seconds' : 'time'),
    required,
    readOnly,
    disabled,
    onChange,
    onFocus,
    onBlur,
    onResolveSuggestion,
    autoFocus,
    additionalInfo,
    className,
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getTimeInputTestIds);
  const date = value !== undefined && value !== '' ? parseToDate(value) : undefined;
  const is12h = clockFormat ? clockFormat === 12 : is12HClockFormat(locale);

  const [hourInputRef, minuteInputRef, secondInputRef, meridiemSelectRef] = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLSelectElement>(null)
  ];

  const [hasValue, setHasValue] = useState(!!date);
  useEffect(() => {
    setHasValue(
      !!(
        hourInputRef.current?.value ||
        minuteInputRef.current?.value ||
        (withSeconds && secondInputRef.current?.value)
      )
    );
  }, [date]);

  const { description, descriptionID } = useAutoFocusNextInput([
    hourInputRef,
    minuteInputRef,
    secondInputRef
  ]);

  const pickParts = () => {
    const parts: TimeParts<string | undefined> = {
      hour: hourInputRef.current?.value || undefined,
      minute: minuteInputRef.current?.value || undefined
    };
    if (withSeconds) parts.second = secondInputRef.current?.value;
    if (is12h) parts.dayPeriod = meridiemSelectRef.current?.value;
    return parts;
  };

  const currentlySelectedTime = () => {
    const parts = pickParts();

    if (Object.values(parts).every(Boolean)) {
      const hour = Number(parts.hour);
      const minute = Number(parts.minute);
      const second = parts.second ? Number(parts.second) : 0;

      const current = date !== undefined ? new Date(date) : getAdjustedUTCDateTime();
      return validateTime(hour, minute, second, 0, is12h)
        ? new Date(
            current.setUTCHours(
              is12h ? convertTo24h(hour, parts.dayPeriod!) : hour,
              minute,
              second,
              date?.getUTCMilliseconds() || 0
            )
          )
        : undefined;
    }
  };

  const [open, setOpen, , setPopoverRef, timePickerButtonEl, setTimePickerButtonEl] =
    usePickerButton();

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const onFocusChange = (focused: boolean) => {
    const callbackParam = convertToCallbackParameter(
      date ? new Date(date) : new Date(),
      pickParts()
    );
    if (onFocus && focused) onFocus(callbackParam);
    if (!focused) {
      if (inputContainerRef.current) inputContainerRef.current.scrollTop = 0;
      onBlur?.(callbackParam);
    }
  };

  const onInputChange = () => {
    onChange?.(convertToCallbackParameter(date ? new Date(date) : new Date(), pickParts()));
    setHasValue(
      !!(
        hourInputRef.current?.value ||
        minuteInputRef.current?.value ||
        (withSeconds && secondInputRef.current?.value)
      )
    );
  };

  useEffect(() => {
    if (is12h && meridiemSelectRef.current) {
      meridiemSelectRef.current.value = getMeridiem(date?.getUTCHours());
    }
  }, [is12h, date?.getUTCHours()]);

  const displayNames = new Intl.DisplayNames(locale, { style: 'long', type: 'dateTimeField' });

  const hour = date && is12h ? convertTo12h(date.getUTCHours()) : date?.getUTCHours();
  const HourInput = (
    <PartInput
      data-testid={testIds.controlHour}
      key='hour'
      placeholder={t('hour_placeholder')}
      aria-label={displayNames.of('hour')}
      value={hour}
      onChange={onInputChange}
      size={2}
      maxLength={2}
      readOnly={readOnly}
      min={is12h ? 1 : 0}
      max={is12h ? 12 : 23}
      ref={hourInputRef}
      autoFocus={autoFocus}
    />
  );

  const MinuteInput = (
    <PartInput
      data-testid={testIds.controlMinute}
      key='minute'
      placeholder={t('minute_placeholder')}
      aria-label={displayNames.of('minute')}
      value={date?.getUTCMinutes()}
      onChange={onInputChange}
      size={2}
      maxLength={2}
      readOnly={readOnly}
      max={59}
      ref={minuteInputRef}
    />
  );

  const SecondInput = (
    <PartInput
      data-testid={testIds.controlSecond}
      key='second'
      placeholder={t('second_placeholder')}
      aria-label={displayNames.of('second')}
      value={date?.getUTCSeconds()}
      onChange={onInputChange}
      size={2}
      maxLength={2}
      readOnly={readOnly}
      max={59}
      ref={secondInputRef}
    />
  );

  const Period = (
    <Select
      data-testid={testIds.controlPeriod}
      ref={meridiemSelectRef}
      aria-label={displayNames.of('dayPeriod')}
      key='period'
      required={required}
      disabled={disabled}
      onChange={() => {
        if (Object.values(pickParts()).every(Boolean)) onInputChange();
      }}
      readOnly={readOnly}
    >
      <Option value='AM'>{t('meridiem_value_am')}</Option>
      <Option value='PM'>{t('meridiem_value_pm')}</Option>
    </Select>
  );

  const containerRef = useConsolidatedRef(ref);
  useFocusWithin([containerRef], onFocusChange);

  const Picker = (
    <>
      <StyledInputButton
        data-testid={testIds.openClosePicker}
        variant='simple'
        icon
        ref={setTimePickerButtonEl}
        onClick={() => setOpen(cur => !cur)}
        aria-label={t(open ? 'close_time_button_a11y' : 'open_time_button_a11y')}
        readOnly={readOnly}
        disabled={disabled}
      >
        <Icon name='clock' />
      </StyledInputButton>

      <Popover
        show={open}
        ref={setPopoverRef}
        target={containerRef.current}
        placement='bottom-end'
        strategy='fixed'
        modifiers={[sameWidth]}
      >
        <TimePicker
          testId={testIds.picker}
          selected={currentlySelectedTime()}
          interval={pickerInterval}
          withSeconds={withSeconds}
          onSelect={selected => {
            hourInputRef.current!.value = (
              is12h ? convertTo12h(selected.getUTCHours()) : selected.getUTCHours()
            )
              .toString()
              .padStart(2, '0');
            minuteInputRef.current!.value = selected.getUTCMinutes().toString().padStart(2, '0');
            if (withSeconds)
              secondInputRef.current!.value = selected.getUTCSeconds().toString().padStart(2, '0');
            if (meridiemSelectRef.current) {
              meridiemSelectRef.current.value = getMeridiem(selected.getUTCHours());
            }
            setOpen(false);
            setTimeout(() => timePickerButtonEl?.focus());
            onInputChange();
          }}
          clockFormat={clockFormat}
        />
      </Popover>
    </>
  );

  const Comp = (
    <Flex
      data-testid={label ? undefined : testIds.root}
      as={StyledTimeInput}
      forwardedAs={StyledFormControl}
      hasSuggestion={status === 'pending' && !!onResolveSuggestion}
      container={{ alignItems: 'center', wrap: 'nowrap', justify: 'between' }}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
      ref={containerRef}
      onClick={(e: MouseEvent) => {
        if (e.target === containerRef.current) hourInputRef.current?.focus();
      }}
      {...restProps}
      className={createClassName('time-input', className)}
    >
      <Flex
        ref={inputContainerRef}
        as={StyledInputContainer}
        container={{ alignItems: 'center', wrap: 'wrap' }}
      >
        {getDateTimeParts(locale, getTimeOptions(withSeconds, is12h)).map(part => {
          switch (part) {
            case 'hour':
              return HourInput;
            case 'minute':
              return MinuteInput;
            case 'second':
              return SecondInput;
            case 'dayPeriod':
              return Period;
            default:
              return part;
          }
        })}
      </Flex>
      {!readOnly && (
        <Flex container>
          {hasValue && (
            <StyledInputButton
              data-testid={testIds.clearCurrentTime}
              aria-label={t('clear_current_time')}
              icon
              onClick={() => {
                hourInputRef.current!.value = '';
                minuteInputRef.current!.value = '';
                if (withSeconds) secondInputRef.current!.value = '';
                if (is12h) meridiemSelectRef.current!.value = t('meridiem_value_am');
                setTimeout(() => timePickerButtonEl?.focus());
                onInputChange();
              }}
              variant='simple'
              compact
              label={t('clear')}
            >
              <Icon name='times' />
            </StyledInputButton>
          )}
          {Picker}
        </Flex>
      )}
    </Flex>
  );

  return label ? (
    <>
      <FormField
        testId={testIds}
        as='fieldset'
        label={label}
        labelHidden={labelHidden}
        id={id}
        info={info}
        readOnly={readOnly}
        status={status}
        required={required}
        disabled={disabled}
        onResolveSuggestion={onResolveSuggestion}
        additionalInfo={additionalInfo}
        aria-describedby={descriptionID}
        ref={ref}
      >
        {Comp}
      </FormField>
      {description}
    </>
  ) : (
    Comp
  );
});

export default withTestIds(TimeInput, getTimeInputTestIds);
